import { useParams, Link } from 'react-router-dom';
import { allContent, categories } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const items = allContent.filter((item) => item.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类不存在</h2>
          <Link to="/" className="text-primary-600 text-sm hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 中文字幕日韩影视大全`,
            description: category.description,
            url: `https://grcsaas.cn/category/${slug}`,
          }),
        }}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-600 ml-14">{category.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-xs text-foreground-500">分类筛选：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {items.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类暂无内容，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group flex flex-col bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative w-full aspect-[2/3] overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={`${item.title} - ${item.titleEn} 中文字幕`}
                        title={`${item.title} ${item.categoryName}中文字幕`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap">
                        {item.rating}
                      </div>
                      {'updateInfo' in item && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <span className="text-white text-xs font-medium whitespace-nowrap">{item.updateInfo}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 flex flex-col gap-1.5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {item.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}